// ═══════════════════════════════════════════════════════════════
// COMPLIANCE POLICIES — Index (Batch 15)
// ═══════════════════════════════════════════════════════════════
// Aggregates all 14 compliance policy documents.
// Each policy is keyed by its `id` field for routing.
// ═══════════════════════════════════════════════════════════════

import { SAFETY_MANAGEMENT_SYSTEM } from './safetyManagementSystem';
import { DATA_BREACH_RESPONSE_PLAN } from './dataBreachResponsePlan';
import { VULNERABLE_PASSENGER_POLICY } from './vulnerablePassengerPolicy';
import { CHILD_SAFETY_POLICY } from './childSafetyPolicy';
import { ACCESSIBILITY_POLICY } from './accessibilityPolicy';
import { ANTI_DISCRIMINATION_POLICY } from './antiDiscriminationPolicy';
import { EMERGENCY_RESPONSE_PROCEDURE } from './emergencyResponseProcedure';
import { FRAUD_PREVENTION_POLICY } from './fraudPreventionPolicy';
import { CONSENT_POLICY } from './consentPolicy';
import { DATA_RETENTION_POLICY } from './dataRetentionPolicy';
import { DRIVER_CODE_OF_CONDUCT } from './driverCodeOfConduct';
import { COMPLAINTS_HANDLING_POLICY } from './complaintsHandlingPolicy';
import { INCIDENT_REPORTING_POLICY } from './incidentReportingPolicy';
import { DRIVER_SUSPENSION_POLICY } from './driverSuspensionPolicy';

export const ALL_POLICIES = [
  // Tier 1 — Critical (BSP application priority)
  SAFETY_MANAGEMENT_SYSTEM,
  DATA_BREACH_RESPONSE_PLAN,
  VULNERABLE_PASSENGER_POLICY,
  CHILD_SAFETY_POLICY,
  ACCESSIBILITY_POLICY,
  // Tier 2 — High-priority
  ANTI_DISCRIMINATION_POLICY,
  EMERGENCY_RESPONSE_PROCEDURE,
  FRAUD_PREVENTION_POLICY,
  CONSENT_POLICY,
  DATA_RETENTION_POLICY,
  // Tier 3 — Extracted operational policies
  DRIVER_CODE_OF_CONDUCT,
  COMPLAINTS_HANDLING_POLICY,
  INCIDENT_REPORTING_POLICY,
  DRIVER_SUSPENSION_POLICY,
];

// Get a single policy by ID
export function getPolicyById(id) {
  return ALL_POLICIES.find(p => p.id === id) || null;
}

// Filter policies by audience
//   audience parameter: 'rider' | 'driver' | 'admin' | 'public'
//   Returns policies appropriate to the audience.
//
// Rule:
//   - 'public' (unauthenticated visitor) → sees only audience: 'all'
//   - 'rider' → sees audience: 'all'
//   - 'driver' → sees audience: 'all' or 'driver' or 'internal'
//   - 'admin' → sees everything
export function getPoliciesForAudience(audience) {
  if (audience === 'admin' || audience === 'superAdmin') return ALL_POLICIES;
  if (audience === 'driver') {
    return ALL_POLICIES.filter(p => ['all', 'driver', 'internal'].includes(p.audience));
  }
  if (audience === 'rider') {
    return ALL_POLICIES.filter(p => p.audience === 'all');
  }
  // 'public' / unauthenticated / anything else
  return ALL_POLICIES.filter(p => p.audience === 'all');
}

// Group policies by category (for index page display)
export const POLICY_CATEGORIES = [
  {
    title: 'Safety & Risk Management',
    policyIds: ['safety-management-system', 'incident-reporting-policy', 'emergency-response-procedure'],
  },
  {
    title: 'Passenger Care',
    policyIds: ['vulnerable-passenger-policy', 'child-safety-policy', 'accessibility-policy'],
  },
  {
    title: 'Conduct & Equal Treatment',
    policyIds: ['driver-code-of-conduct', 'anti-discrimination-policy', 'driver-suspension-policy'],
  },
  {
    title: 'Privacy & Data',
    policyIds: ['consent-policy', 'data-retention-policy', 'data-breach-response-plan'],
  },
  {
    title: 'Integrity & Operations',
    policyIds: ['fraud-prevention-policy', 'complaints-handling-policy'],
  },
];